import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { Prediction, ForecastResults } from '../../types';

interface CashFlowTableProps {
  prediction: Prediction;
  purchasePrice?: number;
}

export const CashFlowTable: React.FC<CashFlowTableProps> = ({ prediction, purchasePrice }) => {
  const tableData = useMemo(() => {
    if (!prediction.results) return [];

    try {
      const results: ForecastResults = JSON.parse(prediction.results);
      const forecasts = results.aggregatedForecastedReturns;

      if (!forecasts || forecasts.length === 0) return [];

      return forecasts.slice(0, 24).map((point, index) => {
        const previousCumulative = index > 0 ? forecasts[index - 1].forecastedReturn ?? 0 : 0;
        const cumulativeReturn = point.forecastedReturn ?? 0;
        const periodReturn = cumulativeReturn - previousCumulative;
        const netReturn = cumulativeReturn - (purchasePrice || 0);

        return {
          month: index + 1,
          totalDebt: 0, // Not available from API
          purchasePrice: purchasePrice || 0,
          revenue: periodReturn > 0 ? periodReturn : 0,
          expense: periodReturn < 0 ? periodReturn : 0,
          balance: periodReturn,
          cumulativeBalance: netReturn,
          confidenceLower: point.confidenceIntervalLower ?? 0,
          confidenceUpper: point.confidenceIntervalUpper ?? 0,
        };
      });
    } catch (error) {
      console.error('Failed to parse prediction results:', error);
      return [];
    }
  }, [prediction.results, purchasePrice]);

  if (tableData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Nincs megjeleníthető adat</Typography>
      </Box>
    );
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('hu-HU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Split into two tables of 12 months
  const firstHalf = tableData.slice(0, 12);
  const secondHalf = tableData.slice(12, 24);

  const renderTable = (data: typeof tableData) => (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Hónap</strong></TableCell>
            {data.map((row) => (
              <TableCell key={row.month} align="right">
                <strong>{row.month}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Vételár</TableCell>
            {data.map((row) => (
              <TableCell key={row.month} align="right">
                {formatNumber(row.purchasePrice)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>Bevétel</TableCell>
            {data.map((row) => (
              <TableCell key={row.month} align="right" sx={{ color: row.revenue > 0 ? 'success.main' : 'inherit' }}>
                {formatNumber(row.revenue)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>Ráfordítás</TableCell>
            {data.map((row) => (
              <TableCell key={row.month} align="right" sx={{ color: row.expense < 0 ? 'error.main' : 'inherit' }}>
                {formatNumber(row.expense)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell><strong>Egyenleg</strong></TableCell>
            {data.map((row) => (
              <TableCell
                key={row.month}
                align="right"
                sx={{
                  fontWeight: 'bold',
                  color: row.balance >= 0 ? 'success.main' : 'error.main',
                }}
              >
                {formatNumber(row.balance)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell><strong>Kumulált egyenleg</strong></TableCell>
            {data.map((row) => (
              <TableCell
                key={row.month}
                align="right"
                sx={{
                  fontWeight: 'bold',
                  color: row.cumulativeBalance >= 0 ? 'success.main' : 'error.main',
                }}
              >
                {formatNumber(row.cumulativeBalance)}
              </TableCell>
            ))}
          </TableRow>
          {data[0].confidenceLower !== 0 && (
            <>
              <TableRow>
                <TableCell>Valószínű sáv alja</TableCell>
                {data.map((row) => (
                  <TableCell key={row.month} align="right" sx={{ fontSize: '0.85em', color: 'text.secondary' }}>
                    {formatNumber(row.confidenceLower)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Valószínű sáv teteje</TableCell>
                {data.map((row) => (
                  <TableCell key={row.month} align="right" sx={{ fontSize: '0.85em', color: 'text.secondary' }}>
                    {formatNumber(row.confidenceUpper)}
                  </TableCell>
                ))}
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Cash Flow Táblázat (1-12 hónap)
      </Typography>
      {renderTable(firstHalf)}

      {secondHalf.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Cash Flow Táblázat (13-24 hónap)
          </Typography>
          {renderTable(secondHalf)}
        </>
      )}

      <Typography variant="caption" color="text.secondary">
        Minden érték ezer HUF-ban. A valószínűségi sávok a 80%-os megbízhatósági intervallumot mutatják.
      </Typography>
    </Box>
  );
};
